/**
 * Local Prompt Editor
 *
 * Electron-specific prompt management: browse all registered prompts
 * grouped by category, search, view/edit overrides, preview with
 * sample variables, and diff against defaults.
 *
 * @module @humanizer/studio/components/settings/LocalPromptEditor
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocalAdmin } from '../../hooks/useLocalAdmin';
import { PromptDiffView } from './PromptDiffView';

// ═══════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════

interface PromptEntry {
  id: string;
  name: string;
  description: string;
  template?: string;
  category: string;
  version: number;
  isOverridden: boolean;
  overrideTemplate?: string;
  variables?: string[];
  usedBy?: string[];
  error?: string;
}

// Human-readable category labels
const CATEGORY_LABELS: Record<string, string> = {
  'agent-loop': 'Agent Loop',
  builder: 'Builder',
  curator: 'Curator',
  harvester: 'Harvester',
  reviewer: 'Reviewer',
  vimalakirti: 'Vimalakirti',
  transform: 'Transform',
  utility: 'Utility',
  prospector: 'Prospector',
  refiner: 'Refiner',
  archivist: 'Archivist',
  search: 'Search',
};

// ═══════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════

interface LocalPromptEditorProps {
  /** Categories to hide (e.g. ['vimalakirti'] for non-admin users) */
  hideCategories?: string[];
}

export function LocalPromptEditor({ hideCategories }: LocalPromptEditorProps = {}) {
  const {
    listPrompts, getPrompt, updatePromptOverride, deletePromptOverride,
    previewPrompt, resetAllPromptOverrides,
  } = useLocalAdmin();

  // Data
  const [prompts, setPrompts] = useState<PromptEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // UI state
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editTemplate, setEditTemplate] = useState('');
  const [saving, setSaving] = useState(false);
  const [showDiff, setShowDiff] = useState(false);
  const [previewResult, setPreviewResult] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState(false);

  // ─────────────────────────────────────────────────────────────────
  // DATA FETCHING
  // ─────────────────────────────────────────────────────────────────

  const fetchPrompts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await listPrompts();
      setPrompts(list || []);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [listPrompts]);

  useEffect(() => {
    fetchPrompts();
  }, [fetchPrompts]);

  // ─────────────────────────────────────────────────────────────────
  // DERIVED STATE
  // ─────────────────────────────────────────────────────────────────

  const overrideCount = useMemo(
    () => prompts.filter((p) => p.isOverridden).length,
    [prompts]
  );

  const filteredPrompts = useMemo(() => {
    let list = prompts;
    // Filter out hidden categories (e.g. vimalakirti for non-admin)
    if (hideCategories?.length) {
      const hidden = new Set(hideCategories.map((c) => c.toLowerCase()));
      list = list.filter((p) => !hidden.has((p.category || '').toLowerCase()));
    }
    if (!search) return list;
    const q = search.toLowerCase();
    return list.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        p.id.toLowerCase().includes(q) ||
        p.description.toLowerCase().includes(q) ||
        p.category.toLowerCase().includes(q)
    );
  }, [prompts, search, hideCategories]);

  const groupedPrompts = useMemo(() => {
    const groups: Record<string, PromptEntry[]> = {};
    for (const p of filteredPrompts) {
      const cat = p.category || 'utility';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(p);
    }
    // Sort categories alphabetically
    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
  }, [filteredPrompts]);

  const selected = useMemo(
    () => prompts.find((p) => p.id === selectedId) ?? null,
    [prompts, selectedId]
  );

  // ─────────────────────────────────────────────────────────────────
  // ACTIONS
  // ─────────────────────────────────────────────────────────────────

  const selectPrompt = useCallback(
    async (id: string) => {
      const prompt = prompts.find((p) => p.id === id);
      if (!prompt) return;
      setSelectedId(id);
      setShowDiff(false);
      setPreviewResult(null);
      // Fetch full effective prompt (includes resolved template text)
      try {
        const effective = await getPrompt(id);
        if (effective && !effective.error && effective.template) {
          setEditTemplate(effective.template);
          // Store the default template for diff/revert
          prompt.template = effective.defaultTemplate ?? effective.template;
          if (effective.isOverridden) {
            prompt.overrideTemplate = effective.template;
          }
        } else if (effective?.error) {
          setError(effective.error);
        }
      } catch (err) {
        setError(`Failed to load prompt: ${(err as Error).message}`);
      }
    },
    [prompts, getPrompt]
  );

  const handleSave = useCallback(async () => {
    if (!selectedId || !editTemplate.trim()) return;
    setSaving(true);
    setError(null);
    try {
      await updatePromptOverride(selectedId, editTemplate);
      await fetchPrompts();
      // Keep selection, update local state
      const updated = prompts.find((p) => p.id === selectedId);
      if (updated) {
        setEditTemplate(editTemplate);
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  }, [updatePromptOverride, selectedId, editTemplate, fetchPrompts, prompts]);

  const handleRevert = useCallback(async () => {
    if (!selectedId) return;
    setSaving(true);
    setError(null);
    try {
      await deletePromptOverride(selectedId);
      await fetchPrompts();
      // Re-fetch the effective prompt to get the default template
      const effective = await getPrompt(selectedId);
      if (effective && !effective.error && effective.template) {
        setEditTemplate(effective.template);
      }
      setShowDiff(false);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  }, [deletePromptOverride, selectedId, fetchPrompts, getPrompt]);

  const handlePreview = useCallback(async () => {
    if (!selectedId) return;
    setPreviewing(true);
    setPreviewResult(null);
    try {
      const result = await previewPrompt(selectedId);
      setPreviewResult(result?.preview ?? result?.error ?? 'No preview available');
    } catch (err) {
      setPreviewResult(`Error: ${(err as Error).message}`);
    } finally {
      setPreviewing(false);
    }
  }, [previewPrompt, selectedId]);

  const handleResetAll = useCallback(async () => {
    if (!confirm('Reset all prompt overrides to defaults? This cannot be undone.')) return;
    setSaving(true);
    try {
      await resetAllPromptOverrides();
      await fetchPrompts();
      setSelectedId(null);
      setEditTemplate('');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  }, [resetAllPromptOverrides, fetchPrompts]);

  // ─────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="settings-section">
        <div className="settings-loading">
          <span className="settings-loading__spinner" />
        </div>
      </div>
    );
  }

  return (
    <div className="settings-section">
      <div className="settings-section__header">
        <div>
          <h2 className="settings-section__title">Prompt Editor</h2>
          <p className="settings-section__description">
            Customize LLM prompts used by agents and transformations.
            {' '}{overrideCount} of {prompts.length} customized.
          </p>
        </div>
        {overrideCount > 0 && (
          <button
            className="btn btn--ghost btn--sm"
            onClick={handleResetAll}
            disabled={saving}
          >
            Reset All
          </button>
        )}
      </div>

      <div className="settings-alert settings-alert--warning">
        <strong>Advanced Feature.</strong> Modifying prompts can break agent
        and transformation operations. Edit with care — small wording changes
        can cause unexpected behavior. Use &ldquo;Revert to Default&rdquo; to
        undo changes to any prompt.
      </div>

      {error && (
        <div className="settings-alert settings-alert--error">{error}</div>
      )}

      <div className="settings-section__content">
        <div className="prompt-editor">
          {/* Left: Prompt List */}
          <div className="prompt-editor__list">
            <div className="prompt-editor__search">
              <input
                type="text"
                className="settings-form-group__input"
                placeholder="Search prompts..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <div className="prompt-editor__categories">
              {groupedPrompts.map(([category, items]) => (
                <div key={category} className="prompt-editor__category">
                  <h4 className="prompt-editor__category-title">
                    {CATEGORY_LABELS[category] || category}
                    <span className="prompt-editor__category-count">
                      {items.length}
                    </span>
                  </h4>
                  <ul className="prompt-editor__items">
                    {items.map((prompt) => (
                      <li key={prompt.id}>
                        <button
                          type="button"
                          className={`prompt-editor__item ${
                            selectedId === prompt.id ? 'prompt-editor__item--selected' : ''
                          } ${prompt.isOverridden ? 'prompt-editor__item--overridden' : ''}`}
                          onClick={() => selectPrompt(prompt.id)}
                        >
                          <span className="prompt-editor__item-name">
                            {prompt.name}
                          </span>
                          {prompt.isOverridden && (
                            <span className="prompt-editor__item-badge">edited</span>
                          )}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}

              {groupedPrompts.length === 0 && (
                <p className="settings-empty-text">No prompts match your search.</p>
              )}
            </div>
          </div>

          {/* Right: Detail Panel */}
          <div className="prompt-editor__detail">
            {selected ? (
              <>
                <div className="prompt-editor__detail-header">
                  <h3 className="settings-card__title">{selected.name}</h3>
                  <p className="prompt-editor__detail-meta">
                    {selected.description ?? ''}
                    {' '}
                    <span className="prompt-editor__detail-id">({selected.id})</span>
                  </p>
                </div>

                {/* Template Editor */}
                <div className="prompt-editor__template">
                  {showDiff && selected.isOverridden ? (
                    <PromptDiffView
                      defaultTemplate={selected.template ?? ''}
                      overrideTemplate={selected.overrideTemplate ?? editTemplate}
                    />
                  ) : (
                    <textarea
                      className="prompt-editor__textarea"
                      value={editTemplate}
                      onChange={(e) => setEditTemplate(e.target.value)}
                      rows={16}
                      spellCheck={false}
                    />
                  )}
                </div>

                {/* Preview */}
                {previewResult && (
                  <div className="prompt-editor__preview">
                    <h4 className="prompt-editor__preview-title">Preview (sample variables)</h4>
                    <pre className="prompt-editor__preview-content">{previewResult}</pre>
                  </div>
                )}

                {/* Actions */}
                <div className="prompt-editor__actions">
                  <button
                    className="btn btn--primary btn--sm"
                    onClick={handleSave}
                    disabled={saving || editTemplate === selected.template}
                  >
                    {saving ? 'Saving...' : 'Save Override'}
                  </button>

                  {selected.isOverridden && (
                    <>
                      <button
                        className="btn btn--ghost btn--sm"
                        onClick={handleRevert}
                        disabled={saving}
                      >
                        Revert to Default
                      </button>
                      <button
                        className="btn btn--ghost btn--sm"
                        onClick={() => setShowDiff(!showDiff)}
                      >
                        {showDiff ? 'Edit' : 'Show Diff'}
                      </button>
                    </>
                  )}

                  <button
                    className="btn btn--ghost btn--sm"
                    onClick={handlePreview}
                    disabled={previewing}
                  >
                    {previewing ? 'Loading...' : 'Preview'}
                  </button>
                </div>
              </>
            ) : (
              <div className="prompt-editor__empty">
                <p className="settings-empty-text">
                  Select a prompt from the list to view or edit it.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
